import { AbstractGameController } from "@lib/titan2/dist/game/controller/AbstractGameController";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { TweenConstants } from "../view/abstract/constant/TweenConstants";
import { FreeSpinOverlayEvent, ReelEvent, SlingoEvent } from "./event/SlingoEvent";
/**
 * ReelController.ts
 */
export class ReelController extends AbstractGameController {
    create() {
        super.create();
        Logger.addObjectRef(this, "ReelController");
    }
    createModel() {
        super.createModel();
        this.model = this.baseModel;
        this.reelDisplayData = this.model.getReelDisplayData();
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(SlingoEvent.SPIN_RESULT, this.handleSpinResult);
        this.registerGame(SlingoEvent.DAB_SUCCESS, this.handleDabSuccess);
        this.registerGame(CommonGameEvent.STANDARD_SPIN_RECOVERY, this.handleRecovery);
        this.registerGame(CommonGameEvent.FREE_SPIN_RECOVERY, this.handleRecovery);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY, this.handleRecovery);
        this.registerGame(SlingoEvent.DAB_COMPLETE, this.handleDabComplete);
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        this.registerGame(SlingoEvent.GAME_SUMMARY, this.showReels);
        this.registerGame(SlingoEvent.FREE_SPIN_AWARD_NEXT, this.handleFreeSpinAwardNext);
        this.registerGame(SlingoEvent.FREE_SPIN_AWARD_COMPLETE, this.handleFreesSpinAwardComplete);
        this.registerGame(ReelEvent.REEL_STOPPED, this.handleReelStopped);
        this.registerGame(FreeSpinOverlayEvent.FREE_SPIN_END, this.handleFreeSpinEnd);
    }
    handlePurchaseEntry() {
        this.dispatchGame(ReelEvent.RESET);
    }
    handleSpinResult() {
        this.model.setScatterCount(0);
        this.reelDisplayData.reset();
        let delay = 0;
        delay += 50;
        this.dispatchGame(SlingoEvent.REELS_START);
        for (let i = 0; i < this.reelDisplayData.getNumberOfReels(); i++) {
            this.dispatchGame(ReelEvent.SPIN_REEL, delay);
            delay += 50;
        }
        this.callAfter(delay, this.handleSpinReelsComplete);
        this.showReels();
    }
    handleSpinReelsComplete() {
        if (this.model.getReelDisplayData().isStartComplete()) {
            let sequence = this.model.getSpinResult().getReelSequence();
            this.reelDisplayData.setDisplaySymbols(sequence.getSymbols());
            this.stopReels();
        }
    }
    stopReels() {
        if (this.model.getReelDisplayData().isStopReady()) {
            this.stopReel();
        }
    }
    stopReel() {
        this.dispatchGame(ReelEvent.STOP_REEL);
    }
    handleReelStopped() {
        const scatterThreshold = this.model.getScatterThreshold();
        let stopIndex = this.model.getReelDisplayData().getStopIndex();
        if (stopIndex !== undefined) {
            let symbol = this.reelDisplayData.getDisplaySymbols()[stopIndex];
            if (symbol.isJokerOrSuperJoker()) {
                this.model.setScatterCount(this.model.getScatterCount() + 1);
            }
            this.handleReelSymbol(stopIndex, symbol);
            this.model.getReelDisplayData().stopReel();
            if (this.model.getReelDisplayData().isStopComplete()) {
                this.handleReelAwards();
            }
            else if (scatterThreshold >= 0 && this.model.getScatterCount() >= scatterThreshold) {
                this.callAfter(TweenConstants.REEL_STOP_INTERVAL_SUSPENSE, this.stopReel);
            }
            else {
                const extraSpins = this.model.getGameProgress().getSpinsRemaining() === 0;
                const delay = extraSpins ? TweenConstants.REEL_STOP_INTERVAL_PURCHASE : TweenConstants.REEL_STOP_INTERVAL_STANDARD;
                this.callAfter(delay, this.stopReel);
            }
        }
    }
    handleReelAwards() {
        this.dispatchGame(SlingoEvent.REELS_AWARD);
        const sequence = this.model.getSpinResult().getReelSequence();
        this._freeSpinsDone = !sequence.getFreeSpinIndexes().length;
        this.determineReelsAwardBehaviour();
    }
    determineReelsAwardBehaviour() {
        if (!this._freeSpinsDone) {
            this.handleFreeSpins();
        }
        else {
            this.dispatchGame(SlingoEvent.REELS_COMPLETE);
        }
    }
    handleFreeSpins() {
        const spin = this.model.getSpinResult();
        const freeSpinIndexes = spin.getReelSequence().getFreeSpinIndexes();
        let delay = 100;
        for (let i = 0; i < freeSpinIndexes.length; i++) {
            this.callAfter(delay, this.handleFreeSpin);
            delay += 500;
        }
        this._freeSpinAwardIndex = 0;
        this._freeSpinAwardCounter = freeSpinIndexes.length;
    }
    handleFreeSpinAwardNext() {
        const spin = this.model.getSpinResult();
        const freeSpinIndexes = spin.getReelSequence().getFreeSpinIndexes();
        if (freeSpinIndexes[this._freeSpinAwardIndex] >= 0) {
            this._freeSpinAwardIndex++;
            this.dispatchGame(SlingoEvent.FREE_SPIN_AWARD);
        }
    }
    handleFreeSpin() {
        this.dispatchGame(SlingoEvent.FREE_SPIN_AWARD_NEXT);
    }
    handleReelSymbol(index, symbol) {
        const darkenedReels = this.model.getReelDisplayData().getDarkenedReels();
        this.dispatchGame(SlingoEvent.REEL_STRIKE);
        if (symbol.isJoker()) {
            if (darkenedReels.indexOf(index) >= 0) {
                this.reelDisplayData.setDisabledIndexes([index]);
                this.dispatchGame(ReelEvent.DISABLE_REELS);
            }
            else {
                this.dispatchGame(SlingoEvent.REEL_STRIKE_JOKER);
            }
        }
        else if (symbol.isSuperJoker()) {
            this.dispatchGame(SlingoEvent.REEL_STRIKE_SUPER_JOKER);
        }
        else if (symbol.isFreeSpin()) {
            this.dispatchGame(SlingoEvent.REEL_STRIKE_FREE_SPIN);
        }
        else if (symbol.isDevil()) {
            this.dispatchGame(SlingoEvent.REEL_STRIKE_DEVIL);
        }
        else if (symbol.isBonus()) {
            this.dispatchGame(SlingoEvent.REEL_STRIKE_BONUS);
        }
        else if (symbol.isCollectable()) {
            this.dispatchGame(SlingoEvent.REEL_STRIKE_COLLECTABLE);
        }
        else {
            this.dispatchGame(SlingoEvent.REEL_STRIKE_NUMBER);
        }
    }
    showReels() {
        this.dispatchGame(ReelEvent.SHOW);
    }
    handleFreesSpinAwardComplete() {
        this._freeSpinsDone = true;
        this.determineReelsAwardBehaviour();
    }
    handleDabSuccess() {
        const dabData = this.model.getDabData();
        const selectedCell = dabData.getSelectedCell();
        const column = dabData.isSuperJokerCell(selectedCell) ? dabData.getSuperJokerIndex() : selectedCell.getColumn();
        this.reelDisplayData.setDisabledIndexes([column]);
        if (column <= this.reelDisplayData.getNumberOfReels()) {
            this.dispatchGame(ReelEvent.DISABLE_REELS);
        }
    }
    handleDabComplete() {
        this.disableAllReels();
    }
    handleRecovery() {
        let spin = this.model.getSpinResult();
        if (spin) {
            let sequence = spin.getReelSequence();
            if (sequence) {
                let symbols = sequence.getSymbols();
                this.reelDisplayData.setDisplaySymbols(symbols);
                this.dispatchGame(ReelEvent.RECOVER);
                this.darkenReels();
            }
        }
        else {
            this.dispatchGame(ReelEvent.RESET);
        }
    }
    darkenReels() {
        if (this.model.isSpin()) {
            this.disableAllReels();
        }
        else {
            let darkenedReels = this.model.getReelDisplayData().getDarkenedReels();
            this.reelDisplayData.setDisabledIndexes(darkenedReels);
            this.dispatchGame(ReelEvent.DISABLE_REELS);
        }
    }
    disableAllReels() {
        this.reelDisplayData.setDisabledIndexes([0, 1, 2, 3, 4, 5]);
        this.dispatchGame(ReelEvent.DISABLE_REELS);
    }
    handleFreeSpinEnd() {
        this._freeSpinAwardCounter--;
        if (this._freeSpinAwardCounter <= 0) {
            this.handleFreeSpinsComplete();
        }
    }
    handleFreeSpinsComplete() {
        this.callAfter(1000, () => {
            this.dispatchGame(SlingoEvent.FREE_SPIN_AWARD_COMPLETE);
        });
    }
}
//# sourceMappingURL=ReelController.js.map